import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronLeft, AlertTriangle, Pause } from 'lucide-react';
interface Episode5NavScreenProps {
  onBack: () => void;
  onNextEpisode: () => void;
}
export function Episode5NavScreen({
  onBack,
  onNextEpisode
}: Episode5NavScreenProps) {
  const [hasWatched, setHasWatched] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  const [canDismiss, setCanDismiss] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const handlePlayFullscreen = useCallback(async () => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video || !container) return;
    setIsPlaying(true);
    setShowPause(false);
    try {
      if (container.requestFullscreen) {
        await container.requestFullscreen();
      } else if ((container as any).webkitRequestFullscreen) {
        await (container as any).webkitRequestFullscreen();
      }
      await video.play();
    } catch (err) {
      await video.play();
    }
  }, []);
  useEffect(() => {
    if (hasAutoPlayed) return;
    const timer = setTimeout(() => {
      setHasAutoPlayed(true);
      handlePlayFullscreen();
    }, 800);
    return () => clearTimeout(timer);
  }, [hasAutoPlayed, handlePlayFullscreen]);
  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if ((document as any).webkitFullscreenElement) {
      ;
      (document as any).webkitExitFullscreen();
    }
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => {
      setIsPlaying(false);
      exitFullscreen();
      setTimeout(() => setShowPause(true), 400);
    };
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
        if (video && video.ended) {
          setIsPlaying(false);
          setTimeout(() => setShowPause(true), 400);
        }
      }
    };
    video.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [exitFullscreen]);
  useEffect(() => {
    if (!showPause) {
      setCanDismiss(false);
      return;
    }
    const timer = setTimeout(() => setCanDismiss(true), 2000);
    return () => clearTimeout(timer);
  }, [showPause]);
  const dismissPause = () => {
    if (!canDismiss) return;
    setShowPause(false);
    setHasWatched(true);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    },
    exit: {
      opacity: 0,
      y: -50,
      transition: {
        duration: 0.5
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  return <motion.div className="min-h-screen w-full bg-[var(--bg-secondary)] flex flex-col relative overflow-hidden" variants={containerVariants} initial="hidden" animate="visible" exit="exit" data-id="element-200">
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none mix-blend-overlay" data-id="element-201" />

      <div ref={videoContainerRef} className={`${isPlaying && !showPause ? 'block' : 'hidden'} fixed inset-0 z-50 bg-black flex items-center justify-center`} data-id="element-202">
        <video ref={videoRef} className="w-full h-full object-contain bg-black" controls controlsList="nodownload noplaybackrate" disablePictureInPicture onContextMenu={e => e.preventDefault()} playsInline preload="auto" data-id="element-203">
          <source src="https://cdn.shopify.com/videos/c/o/v/104bd4c59fc94e679b02a3f437663c1f.mp4" type="video/mp4" data-id="element-204" />
        </video>
      </div>

      <AnimatePresence data-id="element-205">
        {showPause && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1.5
      }} className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center px-6 text-center" data-id="element-206">
            <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 0.8,
          duration: 1
        }} className="w-20 h-0.5 bg-[var(--accent)] mb-12 origin-center" data-id="element-207" />

            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 1
        }} className="flex items-center gap-3 mb-6" data-id="element-208">
              <Pause className="w-7 h-7 text-[var(--accent)]" data-id="element-209" />
              <h2 className="text-5xl md:text-8xl font-serif font-bold tracking-widest text-[var(--text-primary)]" data-id="element-210">
                PAUSA FINAL
              </h2>
            </motion.div>

            <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 1.2,
          duration: 0.6
        }} className="w-12 h-px bg-[var(--accent)]/50 mb-10 origin-center" data-id="element-211" />

            <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.5,
          duration: 0.8
        }} className="text-xl md:text-2xl font-serif text-[var(--accent)] leading-relaxed font-semibold mb-4" data-id="element-212">
              Acusen a una persona.
            </motion.p>

            <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.8,
          duration: 0.8
        }} className="text-lg md:text-xl font-serif text-[var(--text-primary)] leading-relaxed italic mb-4" data-id="element-213">
              Escriban 3 pruebas que lo demuestren.
            </motion.p>

            <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 2.2,
          duration: 0.8
        }} className="text-[var(--text-secondary)] text-base font-light tracking-widest uppercase mb-16" data-id="element-214">
              No hay marcha atrás.
            </motion.p>

            <AnimatePresence data-id="element-215">
              {canDismiss && <motion.button initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} onClick={dismissPause} className="px-10 py-4 border border-[var(--accent)] text-[var(--text-primary)] uppercase tracking-[0.2em] text-sm transition-all duration-500 hover:bg-[var(--accent-glow)] hover:scale-105" data-id="element-216">
                  Continuar
                </motion.button>}
            </AnimatePresence>

            <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 3,
          duration: 0.8
        }} className="w-16 h-0.5 bg-[var(--accent)]/30 mt-10 origin-center" data-id="element-217" />
          </motion.div>}
      </AnimatePresence>

      <nav className="w-full p-6 flex justify-between items-center z-30" data-id="element-218">
        <button onClick={onBack} className="flex items-center text-[var(--text-secondary)] hover:text-white transition-colors text-sm tracking-widest uppercase group" data-id="element-219">
          <ChevronLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-220" />
          Volver
        </button>
      </nav>

      <div className="flex-1 flex flex-col justify-center items-center px-6 md:px-12 z-20" data-id="element-221">
        <div className="max-w-4xl w-full mx-auto" data-id="element-222">
          <motion.div variants={itemVariants} className="mb-4" data-id="element-223">
            <span className="text-[var(--text-secondary)] uppercase tracking-[0.2em] text-xs md:text-sm font-medium border-l-2 border-[var(--accent)] pl-4" data-id="element-224">
              Temporada 1 — Caso del Auto Blanco
            </span>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-[var(--text-primary)] mb-8 leading-tight" data-id="element-225">
            EPISODIO 5 — <br className="md:hidden" data-id="element-226" />
            <span className="text-white" data-id="element-227">EL AUTO BLANCO</span>
          </motion.h2>

          <motion.div variants={itemVariants} className="w-24 h-1 bg-[var(--accent)] mb-10" data-id="element-228" />

          <motion.div variants={itemVariants} className="space-y-4 mb-12 max-w-2xl" data-id="element-229">
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-230">
              Una cámara lo captó.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-231">
              Una matrícula lo confirma.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-232">
              Y ahora, ya no hay teorías… solo decisiones.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="mb-12 max-w-2xl" data-id="element-233">
            <h3 className="text-lg font-serif text-[var(--text-secondary)] uppercase tracking-widest mb-6" data-id="element-234">
              ¿Qué ocurre en este episodio?
            </h3>
            <ul className="space-y-3 text-[var(--text-primary)] font-light" data-id="element-235">
              <li className="flex items-center" data-id="element-236">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-237" />
                La revelación del CCTV
              </li>
              <li className="flex items-center" data-id="element-238">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-239" />
                La identificación del auto blanco
              </li>
              <li className="flex items-center" data-id="element-240">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-241" />
                La matrícula LM68 GDE
              </li>
              <li className="flex items-center" data-id="element-242">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-243" />
                La conexión directa con un sospechoso
              </li>
            </ul>
          </motion.div>

          <motion.div variants={itemVariants} className="mb-12" data-id="element-244">
            <button onClick={handlePlayFullscreen} className="flex items-center justify-center px-8 py-4 bg-[var(--accent)] text-white font-medium tracking-widest uppercase hover:bg-[var(--accent-hover)] transition-all duration-300 shadow-[0_0_30px_rgba(139,26,26,0.3)] hover:shadow-[0_0_50px_rgba(139,26,26,0.5)] hover:scale-105" data-id="element-245">
              <Play className="w-5 h-5 mr-3 fill-current" data-id="element-246" />
              {hasWatched ? 'Volver a Ver' : 'Ver Episodio 5'}
            </button>
          </motion.div>

          <AnimatePresence data-id="element-247">
            {hasWatched && <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="space-y-12 max-w-3xl" data-id="element-248">
                <div className="w-full h-px bg-white/10" data-id="element-249" />

                <motion.div className="bg-[var(--bg-card)] border-l-4 border-[var(--accent)] p-6 md:p-8 relative overflow-hidden" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} data-id="element-250">
                  <div className="absolute top-0 right-0 p-4 opacity-5" data-id="element-251">
                    <AlertTriangle className="w-24 h-24" data-id="element-252" />
                  </div>
                  <div className="relative z-10" data-id="element-253">
                    <div className="flex items-center mb-4 text-[var(--accent)]" data-id="element-254">
                      <AlertTriangle className="w-5 h-5 mr-2" data-id="element-255" />
                      <span className="font-bold tracking-widest uppercase text-sm" data-id="element-256">
                        Momento Clave
                      </span>
                    </div>
                    <p className="text-[var(--text-primary)] text-lg mb-2" data-id="element-257">
                      Después de este episodio deberán acusar a una persona.
                    </p>
                    <p className="text-[var(--accent)] font-semibold text-base" data-id="element-258">
                      No hay marcha atrás.
                    </p>
                  </div>
                </motion.div>

                <motion.div className="flex justify-center pt-4 pb-12" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} data-id="element-259">
                  <button onClick={onNextEpisode} className="px-8 py-4 border border-white/10 text-[var(--text-secondary)] uppercase tracking-widest text-sm hover:bg-white/5 hover:text-white hover:border-white/30 transition-all duration-300" data-id="element-260">
                    Continuar al Episodio Final
                  </button>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}